import React, { useRef } from 'react';
import { usePathname } from 'expo-router';
import { ScrollView, StyleSheet } from 'react-native';

import { Aside } from '@expo/html-elements';

import { theme, useNavbarContainerHeight, useResponsive } from '@sg/garnish';

import { useIsLoggedIn } from '../../../context/AuthMachine';
import { AccountMenu } from '../AccountMenu';

import type { AccountTabScreenId } from '../Account.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * The account menu for native wide layouts. It lives in the Account stack layout, so it
 * stays mounted, and keeps its scroll position, while the sections next to it change.
 */
export const AccountSidebar = () => {
  const pathname = usePathname();
  const isLoggedIn = useIsLoggedIn();

  const { currentBreakpoint, match } = useResponsive();
  const { paddingTopStyle: navbarPaddingTopStyle } = useNavbarContainerHeight();

  // ─── Refs ────────────────────────────────────────────────────────────

  // Keeps the last account section while another tab or a modal is on top.
  const accountPathnameRef = useRef(pathname);

  // ─── Derived ─────────────────────────────────────────────────────────

  if (checkIfAccountPathname(pathname)) {
    accountPathnameRef.current = pathname;
  }

  const screenId = getAccountSidebarScreenId(accountPathnameRef.current);
  const shouldRenderSidebar = !currentBreakpoint.isXS && isLoggedIn && screenId !== undefined;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    match([undefined, styles.containerSM, styles.containerMD]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  if (!shouldRenderSidebar) {
    return null;
  }

  return (
    <Aside style={containerStyles}>
      <ScrollView
        contentContainerStyle={navbarPaddingTopStyle}
        showsVerticalScrollIndicator={false}
      >
        <AccountMenu screen={screenId} />
      </ScrollView>
    </Aside>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfAccountPathname(pathname: string) {
  return pathname === '/account' || pathname.startsWith('/account/');
}

/**
 * The sidebar section for an account route, or `undefined` for routes that render without
 * the sidebar, such as the gift card purchase flow and confirmations.
 */
function getAccountSidebarScreenId(pathname: string): AccountTabScreenId | undefined {
  const [, section, subsection] = pathname.split('/').filter(Boolean);

  if (!section) {
    return 'profile';
  }

  if (subsection === 'confirmation') {
    return;
  }

  return SIDEBAR_SCREEN_IDS_BY_SECTION[section];
}

// ─── Constants ───────────────────────────────────────────────────────────────

const SIDEBAR_SCREEN_IDS_BY_SECTION: Partial<Record<string, AccountTabScreenId>> = {
  info: 'profile',
  payments: 'payments',
  'credit-and-promo-codes': 'credit-and-promo-codes',
  'gift-cards': 'gift-cards',
  addresses: 'addresses',
  orders: 'orders',
  favorites: 'favorites',
  'dietary-restrictions': 'dietary-restrictions',
  notifications: 'notifications',
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  containerSM: {
    width: 280,
  },
  containerMD: {
    width: 320,
  },
});
